'use client';
import { useState } from 'react';

import { m } from 'framer-motion';

import Badge from '@mui/material/Badge';
import Avatar from '@mui/material/Avatar';
import SvgIcon from '@mui/material/SvgIcon';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import Button from '@mui/material/Button';

import { fToNow } from 'src/utils/format-time';

import { varHover } from 'src/components/animate';
import { Scrollbar } from 'src/components/scrollbar';
import { usePopover, CustomPopover } from 'src/components/custom-popover';
import { Iconify } from 'src/components/iconify';
import { useAuthContext } from 'src/auth/hooks';
import { useGetNotifications , markAsSeen, clearAllNotifications} from 'src/actions/setup/setupActions';
import Chip from '@mui/material/Chip';
import { SvgColor } from 'src/components/svg-color';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from 'src/components/custom-dialog';
import { EmptyContent } from 'src/components/empty-content';
import { toast } from 'src/components/snackbar';

// ----------------------------------------------------------------------

export function ContactsPopover({ data = [], sx, ...other }) {

 const { employeeId } = useAuthContext();
 const { t } = useTranslation('account');

 const { notificationList, isLoading, error, mutateNotifications } = useGetNotifications(employeeId);



  const popover = usePopover();
  const unreadNotificationsCount = notificationList?.notifications?.filter(notification => notification?.isNew).length || 0;

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const handleOpenConfirmDialog = () => {
    setConfirmDialogOpen(true);
  };


  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
  };

  const handleNotificationClick = async (notification) => {
    const { itemId, projectId, type } = notification?.notificationDetail;

    if (itemId && type) {
      try {
        const result = await markAsSeen(itemId, type, employeeId, projectId);
        if (result.success) {
          console.log('Notification marked as seen');
          mutateNotifications();
        } else {
          console.log('Failed to mark notification as seen');
        }
      } catch (error) {
        console.error('Error marking notification:', error);
      }
    }
  };


  const handleClearNotification = async () => {
      try {
        const result = await clearAllNotifications(employeeId);
        if (result.success) {
          toast.success(t('clearedSuccess'));
          await mutateNotifications();
        } else {
          console.log('Failed to mark notification as seen');
        }
      } catch (error) {
        console.error('Error marking notification:', error);
      }
  };




    const icon = (name) => (
      <img
        src={`/assets/icons/navbar/${name}.svg`}
        alt={name}
        style={{ width: '24px', height: '24px' }}
      />
    );





  return (
    <>
        <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        onClick={popover.onOpen}
        sx={{
          ...(popover.open && { bgcolor: (theme) => theme.vars.palette.action.selected }),
          ...sx,
        }}
        {...other}
      >
        <Badge
          badgeContent={notificationList?.notifications?.length}
          color="error"
          max={9999}
          sx={{
            '.MuiBadge-dot': {
              backgroundColor: 'red',
            },
          }}
        >
          <Iconify icon="feather:bell" />
        </Badge>
      </IconButton>


      <CustomPopover
        open={popover.open}
        anchorEl={popover.anchorEl}
        onClose={popover.onClose}
        slotProps={{
          arrow: { offset: 20 },
        }}
        sx={{mt:1}}
      >
      {(notificationList?.notifications?.length > 0) ? (
        <>
          <Typography variant="h6" sx={{ p: 1.5 }}>
            {/* Contacts <span>({data.length})</span> */}
            {t('notifications')}
            {/* <span>({notificationList?.notifications?.length} New)</span> */}


              <Chip sx={{ ml: 1 }} label={`${notificationList?.notifications?.length} ${t('new')}`} color="info" size="small" />
              <Chip sx={{ ml: 1, cursor: 'pointer' }} label= {t('clearAll')} color="error"  onClick={handleOpenConfirmDialog} size="small" />



            <ConfirmDialog
              open={confirmDialogOpen}
              onClose={handleCloseConfirmDialog}
              title={t('clearAll')}
              content={t('clearConfirm')}
              action={
                <Button
                  variant="contained"
                  color='error'
                  onClick={() => {
                    handleClearNotification();
                    handleCloseConfirmDialog();
                  }}
                >
                  {t('clear')}
                </Button>
              }
            />


          </Typography>

          <Scrollbar sx={{ height: 500, width: 700 }}>
            {notificationList?.notifications.map((notification) => (
              <MenuItem key={notification?.notificationDetail?.itemId} sx={{ p: 1 }}
              onClick={() => handleNotificationClick(notification)}
              >
                {(notification?.notificationDetail?.data?.category === 1
                  || notification?.notificationDetail?.data?.category === 14
                  || notification?.notificationDetail?.data?.category === 19
                  )&& icon('ic-kanban')}
                  {(notification?.notificationDetail?.data?.category === 5 || notification?.notificationDetail?.data?.category === 16) && icon('ic-tour')}
                {(notification?.notificationDetail?.data?.category === 10 || notification?.notificationDetail?.data?.category === 13) && icon('ic-calendar')}
                {(notification?.notificationDetail?.data?.category === 25
                  || notification?.notificationDetail?.data?.category === 23
                  || notification?.notificationDetail?.data?.category === 28
                  || notification?.notificationDetail?.data?.category === 29

                )&& icon('ic-chat')}
                {notification?.notificationDetail?.data?.category === 22 && icon('ic-mail')}


                <ListItemText
                  primary={notification?.notificationDetail?.title}
                  // secondary={contact.status === 'offline' ? fToNow(contact.lastActivity) : ''}
                  secondary={notification?.notificationDetail?.body}

                  primaryTypographyProps={{ typography: 'subtitle2' }}
                  secondaryTypographyProps={{ typography: 'caption', color: 'text.disabled' }}
                />
              </MenuItem>
            ))}
          </Scrollbar>
        </>
      ) : (
          <EmptyContent
          filled
          sx={{ py: 10 }}
          title={t('noNotifications')}
          description= {t('notificationNotAvailable')}
        />
      )}

      </CustomPopover>

    </>
  );
}
